import { PUBNUB_MAX_CHANNELS_COUNT } from 'core/constants';
import { chunk } from 'core_main/globals/helpers';
import { ReadMessage } from 'core_main/sdk/mark_read/types';
import { addUnreadCountsToStore, getStore } from 'core_main/store';
import { UnreadCountsState } from 'core_main/types';
import Pubnub from 'pubnub';
import { getConversationPubNub, getMetaPubNub } from './instance';
import { KUSTOMER_APP_CHAT_SESSION_END_EVENT } from '../constants';
import {
  createConversationChannelGroupString,
  createMetaCustomerChannelString,
} from './channelName';

export const getMessageCountsForConversations = (
  response: Pubnub.FetchMessagesResponse,
  metaCustomerChannelName,
): Promise<UnreadCountsState> => {
  const store = getStore();
  const { settings } = store;
  const { noHistory } = settings;
  const { channels } = response;

  const metaCustomerChannelMessages = channels[metaCustomerChannelName];

  const ignoredIds =
    noHistory && metaCustomerChannelMessages
      ? metaCustomerChannelMessages
          .filter((val) => {
            return val.message.name === KUSTOMER_APP_CHAT_SESSION_END_EVENT;
          })
          .map((val) => val.message.data.id)
      : [];

  const channelNames = Object.keys(channels).filter((channel) => {
    const id = channel.split('.convo-')[1];

    return !channel.includes('ext-meta-cust') && !ignoredIds.includes(id);
  });

  const lastReadMessages = channelNames.reduce((acc, val) => {
    const messages = channels[val];

    const lastReadMessage = [...messages].reverse().find((val) => {
      const { message }: { message: ReadMessage['message'] } = val;
      return message.eventType === 'read';
    });

    if (lastReadMessage) {
      acc.push(lastReadMessage);
    }

    return acc;
  }, [] as ReadMessage[]);

  const counts = {
    total: 0,
    conversations: {},
  };

  return new Promise<UnreadCountsState>((resolve) => {
    if (!lastReadMessages.length) resolve(counts);

    lastReadMessages?.forEach((val, index) => {
      const id = val.channel.split('.convo-')[1];

      const isLast = index === lastReadMessages.length - 1;

      const channelName = val.channel.replace('ext-meta-convo', 'ext-convo');

      getConversationPubNub()?.messageCounts(
        {
          channels: [channelName],
          channelTimetokens: [val?.timetoken as string],
        },
        (res) => {
          if (res && res.channels && res.channels[channelName] !== undefined) {
            counts.conversations[id] = res.channels[channelName];
            counts.total += res.channels[channelName];
          } else {
            console.warn('PubNub messageCounts failed or no data for channel:', channelName);
            counts.conversations[id] = 0;
          }
          if (isLast) resolve(counts);
        },
      );
    });
  });
};

export const fetchUnreadCount = (cb?) => {
  const store = getStore();

  const { orgId, customerId } = store;

  const channelGroup = createConversationChannelGroupString(customerId, orgId);
  const metaCustomerChannel = createMetaCustomerChannelString(
    customerId,
    orgId,
  );

  getConversationPubNub()?.listChannels(
    { channelGroup },
    (response: Pubnub.ListChannelsResponse) => {
      if (!response || !response.channels) {
        console.warn('PubNub listChannels failed or no channels returned');
        if (typeof cb === 'function') cb();
        return;
      }

      const metaChannels = response.channels.map((channel) =>
        channel.replace('ext-convo', 'ext-meta-convo'),
      );
      const channelChunks = chunk(
        [metaCustomerChannel, ...metaChannels],
        PUBNUB_MAX_CHANNELS_COUNT,
      );
      Promise.all<UnreadCountsState>(
        channelChunks.map((channels) => {
          return new Promise((resolve) => {
            getMetaPubNub()?.fetchMessages(
              {
                channels,
                count: 1,
              },
              (status, fetchMessagesResponse) => {
                if (status && status.error) {
                  console.warn('PubNub fetchMessages failed:', status.error);
                  resolve({
                    total: 0,
                    conversations: {},
                  });
                } else {
                  resolve(
                    getMessageCountsForConversations(
                      fetchMessagesResponse,
                      metaCustomerChannel,
                    ),
                  );
                }
              },
            );
          });
        }),
      ).then((countResponses) => {
        const allCounts = countResponses.reduce(
          (accum, count) => {
            Object.assign(accum.conversations, count.conversations);
            accum.total += count.total;
            return accum;
          },
          {
            total: 0,
            conversations: {},
          },
        );
        addUnreadCountsToStore(allCounts);
        if (typeof cb === 'function') cb();
        return allCounts;
      }).catch((error) => {
        console.warn('Error fetching unread counts:', error);
        if (typeof cb === 'function') cb();
      });
    },
  );
};
